import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostJson } from './client'

/**
 * Settings → Easeedesk → Business hours & SLA profiles. Ecran nou, numai in
 * v4 (cerinta CEC L54-L56): calendare de lucru, profile SLA si escaladarea la
 * incalcarea SLA. Capul de api: `SettingsEaseedeskSlaHoursController`.
 */

export type WeekDay = '1' | '2' | '3' | '4' | '5' | '6' | '7'

/** 1 = luni ... 7 = duminica; fiecare zi, intervale ["HH:MM", "HH:MM"]. */
export type Week = Record<WeekDay, [string, string][]>

export interface WorkCalendar {
  ID: number
  name: string
  timezone: string
  week: Week
  holidays: number
}

export type TargetType = 'contract' | 'service' | 'ticket_type'

export interface SlaProfile {
  ID: number
  name: string
  mode: 'always' | 'calendar'
  calendar_id: number | null
  links: Record<TargetType, number[]>
}

export interface BreachRule {
  ID: number
  ticket_type_id: number
  group_id: number
  active: boolean
}

export interface Named {
  ID: number
  name: string
}

export interface SlaHoursShell {
  title: string
  /** false = migrarea tabelelor n-a ajuns inca pe baza asta. */
  ready: boolean
  precedence: TargetType[]
  calendars: WorkCalendar[]
  profiles: SlaProfile[]
  breach_rules: BreachRule[]
  /** Seful fiecarui grup - destinatarul "Team manager" al instiintarilor de SLA. */
  team_managers: {
    /** false = coloana `it_departamente.manager_id` n-a ajuns inca pe baza asta. */
    ready: boolean
    /** `manager_id` 0 = grupul n-are sef. */
    groups: { group_id: number; manager_id: number }[]
  }
  /** Cronul instiintarilor "out of SLA". */
  out_of_sla: {
    ready: boolean
    active: boolean
  }
  options: {
    ticket_types: Named[]
    contracts: Named[]
    services: Named[]
    groups: Named[]
    /** Operatorii activi, de ales ca sef de grup. */
    operators: Named[]
    timezones: string[]
  }
}

export interface Holiday {
  ID: number
  day: string
  name: string
}

export interface SaveAnswer {
  ok?: boolean
  ID?: number
  /** Raspunsul serverului de cronuri, gol cand n-a mers (sau cand a raspuns 200 fara corp). */
  cron?: string
  /** De ce n-a mers cronul; lipseste cand a mers. */
  cron_message?: string
  holidays?: Holiday[]
}

const BASE = '/settings/easeedesk/sla_hours'

export function slaHoursShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_hours'],
    queryFn: () => apiGet<SlaHoursShell>(BASE),
  })
}

export function holidaysQuery(calendar: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_hours', 'holidays', calendar],
    queryFn: () => apiGet<{ holidays: Holiday[] }>(`${BASE}/calendars/${calendar}/holidays`),
    enabled: calendar > 0,
  })
}

export function saveCalendar(id: number, fields: { name: string; timezone: string; week: Week }) {
  return apiPostJson<SaveAnswer>(id > 0 ? `${BASE}/calendars/${id}` : `${BASE}/calendars`, fields)
}

export function removeCalendar(id: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/calendars/${id}/remove`, {})
}

export function addHoliday(calendar: number, day: string, name: string) {
  return apiPostJson<SaveAnswer>(`${BASE}/calendars/${calendar}/holidays`, { day, name })
}

export function addLegalHolidays(calendar: number, year: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/calendars/${calendar}/holidays/legal`, { year })
}

export function removeHoliday(calendar: number, holiday: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/calendars/${calendar}/holidays/${holiday}/remove`, {})
}

export function saveProfile(id: number, fields: { name: string; mode: 'always' | 'calendar'; calendar_id: number | null }) {
  return apiPostJson<SaveAnswer>(id > 0 ? `${BASE}/profiles/${id}` : `${BASE}/profiles`, fields)
}

export function saveProfileLinks(id: number, links: Record<TargetType, number[]>) {
  return apiPostJson<SaveAnswer>(`${BASE}/profiles/${id}/links`, links)
}

export function removeProfile(id: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/profiles/${id}/remove`, {})
}

export function saveBreachRule(id: number, fields: { ticket_type_id: number; group_id: number; active: boolean }) {
  return apiPostJson<SaveAnswer>(id > 0 ? `${BASE}/breach_rules/${id}` : `${BASE}/breach_rules`, fields)
}

export function removeBreachRule(id: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/breach_rules/${id}/remove`, {})
}

/** `managerId` 0 = grupul ramane fara sef. */
export function saveTeamManager(groupId: number, managerId: number) {
  return apiPostJson<SaveAnswer>(`${BASE}/team_managers/${groupId}`, { manager_id: managerId })
}

/** Porneste / opreste cronul instiintarilor "out of SLA". */
export function saveOutOfSla(active: boolean) {
  return apiPostJson<SaveAnswer & { active?: boolean }>(`${BASE}/out_of_sla`, { active })
}

/** Programul clasic, cu care porneste un calendar nou: luni-vineri 09-17. */
export function officeWeek(): Week {
  return {
    '1': [['09:00', '17:00']],
    '2': [['09:00', '17:00']],
    '3': [['09:00', '17:00']],
    '4': [['09:00', '17:00']],
    '5': [['09:00', '17:00']],
    '6': [],
    '7': [],
  }
}

export const WEEK_DAYS: { key: WeekDay; label: string }[] = [
  { key: '1', label: 'Monday' },
  { key: '2', label: 'Tuesday' },
  { key: '3', label: 'Wednesday' },
  { key: '4', label: 'Thursday' },
  { key: '5', label: 'Friday' },
  { key: '6', label: 'Saturday' },
  { key: '7', label: 'Sunday' },
]

/** "Mon-Fri 09:00-17:00" - rezumatul unei saptamani, pentru lista de calendare. */
export function weekSummary(week: Week): string {
  const parts: string[] = []
  let run: { from: string; to: string; hours: string } | null = null
  const short = (key: WeekDay) => WEEK_DAYS.find((d) => d.key === key)?.label.slice(0, 3) ?? key

  const flush = () => {
    if (run && run.hours !== '') parts.push(`${run.from === run.to ? run.from : `${run.from}-${run.to}`} ${run.hours}`)
  }

  for (const { key } of WEEK_DAYS) {
    const hours = (week[key] ?? []).map(([a, b]) => `${a}-${b}`).join(', ')
    if (run && run.hours === hours) {
      run.to = short(key)
    } else {
      flush()
      run = { from: short(key), to: short(key), hours }
    }
  }
  flush()

  return parts.length > 0 ? parts.join('; ') : 'No working hours'
}
